import React, { useState, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  CloudSun, Search, MapPin, Loader2, AlertCircle, Wind, Droplets, Eye, Cloud, Umbrella,
  Sun, CloudRain, CloudSnow, CloudDrizzle, CloudFog, CloudLightning, Zap, ChevronRight,
} from 'lucide-react';

const GEOCODE = 'https://geocoding-api.open-meteo.com/v1/search';
const REVERSE = 'https://geocoding-api.open-meteo.com/v1/reverse';
const FORECAST = 'https://api.open-meteo.com/v1/forecast';

// Open-Meteo weather codes → label + icon
const CODE_MAP = {
  0: { label: 'Clear sky', icon: Sun },
  1: { label: 'Mainly clear', icon: Sun },
  2: { label: 'Partly cloudy', icon: CloudSun },
  3: { label: 'Overcast', icon: Cloud },
  45: { label: 'Fog', icon: CloudFog },
  48: { label: 'Rime fog', icon: CloudFog },
  51: { label: 'Light drizzle', icon: CloudDrizzle },
  53: { label: 'Moderate drizzle', icon: CloudDrizzle },
  55: { label: 'Dense drizzle', icon: CloudDrizzle },
  61: { label: 'Slight rain', icon: CloudRain },
  63: { label: 'Moderate rain', icon: CloudRain },
  65: { label: 'Heavy rain', icon: CloudRain },
  71: { label: 'Slight snow', icon: CloudSnow },
  73: { label: 'Moderate snow', icon: CloudSnow },
  75: { label: 'Heavy snow', icon: CloudSnow },
  80: { label: 'Rain showers', icon: CloudRain },
  81: { label: 'Rain showers', icon: CloudRain },
  82: { label: 'Violent showers', icon: CloudRain },
  95: { label: 'Thunderstorm', icon: CloudLightning },
  96: { label: 'Thunderstorm w/ hail', icon: CloudLightning },
  99: { label: 'Thunderstorm w/ hail', icon: CloudLightning },
};
const codeInfo = (c) => CODE_MAP[c] || { label: '—', icon: Cloud };

const windDirLabel = (deg) => {
  const dirs = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
  return dirs[Math.round(((deg % 360) / 22.5)) % 16];
};

const travelImpact = (current, daily) => {
  const wind = current.wind_speed_10m ?? 0;
  const precip = current.precipitation ?? 0;
  const visibility = (current.visibility ?? 10000) / 1000; // km
  const maxDailyPrecip = Math.max(...(daily.precipitation_sum || [0]).slice(0, 2));
  const dangerCode = [65, 75, 82, 95, 96, 99].includes(current.weather_code);
  const reasons = [];
  let score = 0;
  if (wind >= 50) { score += 3; reasons.push(`High wind ${wind.toFixed(0)} km/h`); }
  else if (wind >= 30) { score += 1; reasons.push(`Moderate wind ${wind.toFixed(0)} km/h`); }
  if (precip >= 5) { score += 3; reasons.push(`Active precipitation ${precip.toFixed(1)} mm`); }
  else if (precip >= 1) { score += 1; reasons.push(`Light precipitation ${precip.toFixed(1)} mm`); }
  if (maxDailyPrecip >= 15) { score += 2; reasons.push(`Heavy rain expected today/tomorrow (${maxDailyPrecip.toFixed(0)} mm)`); }
  if (visibility < 1) { score += 3; reasons.push(`Low visibility ${visibility.toFixed(1)} km`); }
  else if (visibility < 4) { score += 1; reasons.push(`Reduced visibility ${visibility.toFixed(1)} km`); }
  if (dangerCode) { score += 3; reasons.push(codeInfo(current.weather_code).label); }
  if (reasons.length === 0) reasons.push('Calm conditions across wind, precipitation and visibility');
  const level = score >= 5 ? 'High' : score >= 2 ? 'Moderate' : 'Low';
  return { level, reasons };
};

const IMPACT_COLOR = { Low: 'emerald', Moderate: 'amber', High: 'rose' };

const Section = ({ title, children, right }) => (
  <div className="glass-card rounded-2xl border border-slate-800 p-5 space-y-3">
    <div className="flex items-center justify-between">
      <h3 className="font-mono text-[10px] uppercase tracking-widest text-slate-400">{title}</h3>
      {right}
    </div>
    {children}
  </div>
);

const Metric = ({ icon: Icon, label, value, sub, tint = '#00f0ff' }) => (
  <div className="flex items-center gap-3 p-3 rounded-lg border border-slate-800 bg-slate-950/40">
    <Icon className="w-4 h-4 shrink-0" style={{ color: tint }} />
    <div className="flex-1 min-w-0">
      <div className="font-mono text-[9px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className="text-white font-display font-bold text-sm truncate">{value}</div>
      {sub && <div className="font-mono text-[9px] text-slate-500 truncate">{sub}</div>}
    </div>
  </div>
);

export const WeatherView = () => {
  const [query, setQuery] = useState('');
  const [locations, setLocations] = useState([]);
  const [selected, setSelected] = useState(null); // {name, lat, lon, country}
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState('');
  const [lastFetch, setLastFetch] = useState(null);

  const doForecast = useCallback(async (loc) => {
    setLoading(true); setError('');
    try {
      const res = await axios.get(FORECAST, {
        params: {
          latitude: loc.latitude, longitude: loc.longitude,
          current: 'temperature_2m,apparent_temperature,relative_humidity_2m,precipitation,cloud_cover,wind_speed_10m,wind_direction_10m,visibility,weather_code',
          hourly: 'temperature_2m,precipitation_probability,weather_code',
          daily: 'weather_code,temperature_2m_max,temperature_2m_min,precipitation_sum,wind_speed_10m_max',
          timezone: 'auto', forecast_days: 7, forecast_hours: 24,
          wind_speed_unit: 'kmh',
        },
      });
      setData(res.data);
      setLastFetch(new Date());
      setSelected(loc);
    } catch (e) {
      setError(e?.response?.data?.reason || e.message || 'Failed to load forecast.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSearch = async (e) => {
    e && e.preventDefault();
    if (!query.trim()) return;
    setSearching(true); setError(''); setLocations([]);
    try {
      const res = await axios.get(GEOCODE, { params: { name: query.trim(), count: 6, language: 'en', format: 'json' } });
      const results = res.data?.results || [];
      if (results.length === 0) {
        setError(`No location matched "${query.trim()}".`);
      } else if (results.length === 1) {
        await doForecast(results[0]);
      } else {
        setLocations(results);
      }
    } catch (e) {
      setError(e.message || 'Search failed.');
    } finally {
      setSearching(false);
    }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) { setError('Geolocation not supported by this browser.'); return; }
    setSearching(true); setError('');
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const rev = await axios.get(REVERSE, { params: { latitude: pos.coords.latitude, longitude: pos.coords.longitude, language: 'en' } });
          const first = rev.data?.results?.[0];
          const loc = first || { latitude: pos.coords.latitude, longitude: pos.coords.longitude, name: 'Current location', country: '' };
          await doForecast(loc);
        } catch (e) {
          const loc = { latitude: pos.coords.latitude, longitude: pos.coords.longitude, name: 'Current location', country: '' };
          await doForecast(loc);
        } finally {
          setSearching(false);
        }
      },
      (err) => { setError(`Location denied: ${err.message}`); setSearching(false); },
      { enableHighAccuracy: false, timeout: 8000 }
    );
  };

  // Seed with a nice default so the page isn't empty on first paint.
  useEffect(() => {
    doForecast({ latitude: 25.276987, longitude: 55.296249, name: 'Dubai', country: 'United Arab Emirates' });
  }, [doForecast]);

  const current = data?.current;
  const impact = current && data?.daily ? travelImpact(current, data.daily) : null;
  const CurrentIcon = current ? codeInfo(current.weather_code).icon : Cloud;

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-8" data-testid="weather-view">
      <section className="space-y-2">
        <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#00f0ff]/10 border border-[#00f0ff]/25 rounded-full">
          <CloudSun className="w-3 h-3 text-[#00f0ff]" />
          <span className="font-mono text-[10px] text-[#00f0ff] tracking-widest font-semibold uppercase">03 // USEFUL DATA · WEATHER INTELLIGENCE</span>
        </div>
        <h2 className="font-display font-black text-3xl sm:text-4xl text-white tracking-tight uppercase">
          Weather <span className="text-[#00f0ff]">Intelligence</span>
        </h2>
        <p className="text-slate-400 text-xs sm:text-sm font-light leading-relaxed max-w-2xl">
          Real-time atmospheric telemetry powered by Open-Meteo. Search any city, use your live geolocation, and read the travel-impact synthesis derived from current wind, precipitation and visibility.
        </p>
      </section>

      <form onSubmit={handleSearch} className="glass-card rounded-2xl border border-slate-800 p-4 flex flex-col sm:flex-row gap-2" data-testid="weather-search-form">
        <div className="flex-1 relative">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search city (e.g. Dubai, New York, Bengaluru)…"
            className="w-full bg-slate-950/60 border border-slate-800 focus:border-[#00f0ff]/60 rounded pl-9 pr-3 py-2 text-sm text-slate-200 font-mono outline-none"
            data-testid="input-weather-search"
          />
        </div>
        <button type="submit" disabled={searching || !query.trim()} className="px-4 py-2 rounded font-mono text-[10px] uppercase tracking-widest font-bold flex items-center gap-2 bg-[#00f0ff]/15 border border-[#00f0ff]/40 text-[#00f0ff] hover:bg-[#00f0ff] hover:text-slate-950 disabled:opacity-50 transition-all cursor-pointer" data-testid="btn-weather-search">
          {searching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}<span>Search</span>
        </button>
        <button type="button" onClick={useMyLocation} disabled={searching} className="px-4 py-2 rounded font-mono text-[10px] uppercase tracking-widest font-bold flex items-center gap-2 bg-[#ff9e00]/15 border border-[#ff9e00]/40 text-[#ff9e00] hover:bg-[#ff9e00] hover:text-slate-950 disabled:opacity-50 transition-all cursor-pointer" data-testid="btn-weather-mylocation">
          <MapPin className="w-3.5 h-3.5" /><span>My location</span>
        </button>
      </form>

      {locations.length > 0 && (
        <Section title="Multiple matches — pick one">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="weather-loc-choices">
            {locations.map((l) => (
              <button key={`${l.id}-${l.latitude}`} onClick={() => { setLocations([]); doForecast(l); }} className="text-left p-3 rounded border border-slate-800 hover:border-[#00f0ff]/50 hover:bg-[#00f0ff]/5 transition-all cursor-pointer flex items-center justify-between gap-2" data-testid={`loc-${l.id || l.name}`}>
                <div>
                  <div className="text-slate-100 text-xs font-semibold">{l.name}{l.admin1 ? `, ${l.admin1}` : ''}</div>
                  <div className="font-mono text-[9px] text-slate-500 uppercase tracking-widest">{l.country} · {l.latitude.toFixed(2)}, {l.longitude.toFixed(2)}</div>
                </div>
                <ChevronRight className="w-3.5 h-3.5 text-slate-500" />
              </button>
            ))}
          </div>
        </Section>
      )}

      {error && (
        <div className="rounded border border-rose-500/40 bg-rose-500/10 text-rose-300 px-3 py-2 text-xs font-mono flex items-start gap-2" data-testid="weather-error">
          <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" /><span>{error}</span>
        </div>
      )}

      {loading && (
        <div className="glass-card rounded-2xl border border-slate-800 p-8 flex items-center justify-center text-slate-500 font-mono text-xs gap-2" data-testid="weather-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Fetching atmospheric telemetry…
        </div>
      )}

      {!loading && data && current && (
        <>
          {/* Current + impact */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="weather-current">
            <div className="glass-card lg:col-span-2 rounded-2xl border border-[#00f0ff]/30 p-6 flex flex-col sm:flex-row items-start sm:items-center gap-6">
              <CurrentIcon className="w-16 h-16 text-[#00f0ff] shrink-0" />
              <div className="flex-1">
                <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Now in</div>
                <div className="font-display font-black text-2xl text-white">{selected?.name}{selected?.country ? `, ${selected.country}` : ''}</div>
                <div className="mt-3 flex items-baseline gap-3">
                  <span className="font-display font-black text-5xl text-[#00f0ff]">{Math.round(current.temperature_2m)}°</span>
                  <span className="font-mono text-xs text-slate-400 uppercase tracking-widest">{codeInfo(current.weather_code).label}</span>
                </div>
                <div className="font-mono text-[10px] text-slate-500 mt-1">Feels like {Math.round(current.apparent_temperature)}°C</div>
              </div>
            </div>
            {impact && (
              <div className={`glass-card rounded-2xl p-5 border border-${IMPACT_COLOR[impact.level]}-500/40 bg-${IMPACT_COLOR[impact.level]}-500/5`} data-testid="weather-impact">
                <div className="flex items-center gap-2 mb-2">
                  <Zap className={`w-4 h-4 text-${IMPACT_COLOR[impact.level]}-400`} />
                  <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Travel Impact</span>
                </div>
                <div className={`font-display font-black text-2xl text-${IMPACT_COLOR[impact.level]}-300 uppercase`}>{impact.level}</div>
                <ul className="mt-3 space-y-1">
                  {impact.reasons.map((r, i) => (
                    <li key={i} className="font-mono text-[10px] text-slate-400 leading-snug">· {r}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Metrics grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="weather-metrics">
            <Metric icon={Droplets} label="Humidity" value={`${current.relative_humidity_2m}%`} />
            <Metric icon={Wind} label="Wind" value={`${current.wind_speed_10m.toFixed(0)} km/h`} sub={windDirLabel(current.wind_direction_10m)} tint="#ff9e00" />
            <Metric icon={Eye} label="Visibility" value={`${(current.visibility / 1000).toFixed(1)} km`} />
            <Metric icon={Umbrella} label="Precipitation" value={`${current.precipitation.toFixed(1)} mm`} tint="#39ff14" />
            <Metric icon={Cloud} label="Cloud cover" value={`${current.cloud_cover}%`} />
            <Metric icon={Sun} label="Apparent" value={`${Math.round(current.apparent_temperature)}°C`} tint="#ff9e00" />
          </div>

          {/* 24-hour */}
          <Section title="Next 24 hours" right={<span className="font-mono text-[9px] text-slate-500 uppercase tracking-widest">Hourly</span>}>
            <div className="flex gap-2 overflow-x-auto pb-2" data-testid="weather-hourly">
              {(data.hourly?.time || []).slice(0, 24).map((t, i) => {
                const HIcon = codeInfo(data.hourly.weather_code[i]).icon;
                return (
                  <div key={t} className="min-w-[70px] flex flex-col items-center gap-1 p-2 rounded border border-slate-800 bg-slate-950/40">
                    <span className="font-mono text-[9px] text-slate-500">{new Date(t).getHours().toString().padStart(2, '0')}:00</span>
                    <HIcon className="w-4 h-4 text-[#00f0ff]" />
                    <span className="font-display font-bold text-xs text-white">{Math.round(data.hourly.temperature_2m[i])}°</span>
                    <span className="font-mono text-[9px] text-slate-500">{data.hourly.precipitation_probability[i] ?? 0}%</span>
                  </div>
                );
              })}
            </div>
          </Section>

          {/* 7-day */}
          <Section title="Next 7 days">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-2" data-testid="weather-daily">
              {(data.daily?.time || []).map((d, i) => {
                const DIcon = codeInfo(data.daily.weather_code[i]).icon;
                const day = new Date(d).toLocaleDateString(undefined, { weekday: 'short' });
                return (
                  <div key={d} className="p-3 rounded border border-slate-800 bg-slate-950/40 flex flex-col items-center gap-1.5">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400">{day}</span>
                    <DIcon className="w-5 h-5 text-[#00f0ff]" />
                    <div className="font-display font-bold text-white text-sm">
                      {Math.round(data.daily.temperature_2m_max[i])}° / <span className="text-slate-500">{Math.round(data.daily.temperature_2m_min[i])}°</span>
                    </div>
                    <div className="font-mono text-[9px] text-slate-500">{(data.daily.precipitation_sum[i] || 0).toFixed(1)} mm</div>
                  </div>
                );
              })}
            </div>
          </Section>

          <div className="flex flex-wrap items-center justify-between gap-2 text-[10px] font-mono text-slate-500 border-t border-slate-900 pt-3">
            <span>Data source · <a href="https://open-meteo.com/" target="_blank" rel="noreferrer" className="text-[#00f0ff] hover:underline">Open-Meteo</a> (free, no auth required)</span>
            {lastFetch && <span>Last updated · {lastFetch.toLocaleTimeString()}</span>}
          </div>
        </>
      )}
    </motion.div>
  );
};
